/**
 * Alerts inbox (India notification actions as an inbox + toasts, never blocking modals): the bell in the top bar and
 * the panel on Today share one list = P5's escalation inbox merged with live stream events. The bell is always
 * mounted, so it owns the stream wiring and the throttled toast (polite, at most one per 10 s).
 */
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { Link, useNavigate } from 'react-router';
import { Icon } from '@ui/Icon';
import { toast } from '@ui/toast';
import { ApiError, newKey } from '@app/core/http';
import { ago } from '@app/core/format';
import { useSession } from '@app/core/auth';
import { useQueryClient } from '@tanstack/react-query';
import { qk, useJob, useTechMap } from '@app/core/entities';
import { useStreamEvent } from '@app/core/stream';
import type { Job, Page, Tech } from '@app/core/types';
import { toastError, toastInfo, toastOk } from '@app/shell/feedback';
import { Empty } from '@app/stage/bits';
import { actionsFor, clearedBy, fromEvent, fromInbox, hrefFor, type Alert, type AlertAction } from './alerts_model';
import { useAckAlert, useCallTech, useInboxAlerts, useMarkBreak } from './queries';
import './today.css';

// ─────────────────────────────────────────── live alerts store (module-level: survives route changes)
let live: Alert[] = [];
const hidden = new Map<string, number>(); // key → hidden at (ms); an alert raised later shows again
let ver = 0;
const subs = new Set<() => void>();
const bump = () => { ver++; subs.forEach((f) => f()); };
const store = {
  sub: (f: () => void) => { subs.add(f); return () => { subs.delete(f); }; },
  get: () => ver,
  add(a: Alert) { live = [a, ...live.filter((x) => x.key !== a.key)].slice(0, 100); bump(); },
  clear(pred: (a: Alert) => boolean) { const n = live.length; live = live.filter((a) => !pred(a)); if (live.length !== n) bump(); },
  hide(a: Alert) { hidden.set(a.key, Date.now()); bump(); },
  unhide(a: Alert) { hidden.delete(a.key); bump(); },
};
const isHidden = (a: Alert) => { const h = hidden.get(a.key); return h != null && Date.parse(a.at) <= h; };
const same = (a: Alert, b: Alert) => a.group === b.group && ((!!a.job_id && a.job_id === b.job_id) || (!!a.tech_id && a.tech_id === b.tech_id));

/** The merged, de-duplicated open alert list (newest first). */
export function useAlerts() {
  const inbox = useInboxAlerts();
  const v = useSyncExternalStore(store.sub, store.get);
  const alerts = useMemo(() => {
    const out: Alert[] = [];
    for (const r of inbox.data ?? []) { const a = fromInbox(r); if (!out.some((x) => x.key === a.key)) out.push(a); }
    for (const a of live) if (!out.some((x) => x.key === a.key || (x.source === 'inbox' && same(x, a)))) out.push(a);
    return out.filter((a) => !isHidden(a)).sort((a, b) => b.at.localeCompare(a.at));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inbox.data, v]);
  return { alerts, inbox };
}

// ─────────────────────────────────────────── text
export function alertText(a: Alert, job: Job | undefined, techs: Map<string, Tech>): { title: string; sub: string } {
  const techId = a.tech_id ?? job?.assigned_tech_id ?? null;
  const tech = techId ? techs.get(techId) : undefined;
  const jobNo = job?.job_no ?? (a.job_id ? (a.job_id.length > 12 ? `Job ${a.job_id.slice(0, 8)}` : a.job_id) : '');
  const kind = a.kind.replace(/_/g, ' ');
  switch (a.group) {
    case 'tech_stopped':
      if (/no_answer/.test(a.kind)) return { title: `${tech?.first_name ?? 'Tech'} isn't answering the going-check`, sub: [tech?.van_label, jobNo].filter(Boolean).join(' · ') };
      if (/eta_exceeded/.test(a.kind)) return { title: `${tech?.first_name ?? 'Tech'} is past the ETA`, sub: jobNo };
      return { title: `${tech?.van_label ?? tech?.first_name ?? 'A van'} stopped${a.minutes != null ? ` ${a.minutes} min` : ''}`, sub: [tech?.display_name, jobNo && `on the way to ${jobNo}`].filter(Boolean).join(' · ') };
    case 'tech_declined':
      if (/unassigned/.test(a.kind)) return { title: `${jobNo} still needs a tech`, sub: job?.service_summary ?? 'Unassigned past its window' };
      if (/needs_dispatcher|cant/.test(a.kind)) return { title: `${jobNo} needs a dispatcher`, sub: kind };
      return { title: `${tech?.first_name ?? 'Tech'} declined ${jobNo}`, sub: 'Back in the unassigned queue' };
    case 'estimate_declined': return { title: `Estimate declined · ${jobNo}`, sub: job?.customer_name ?? 'Follow up with the customer' };
    case 'anomaly': return { title: `Check ${jobNo}`, sub: kind.replace(/^anomaly /, '') };
    case 'parts_pending': return { title: 'Parts order waiting for approval', sub: 'Review the restock list' };
    case 'job': return { title: jobNo ? `${jobNo}: ${kind}` : kind, sub: job?.customer_name ?? '' };
  }
}

// ─────────────────────────────────────────── one alert row + its actions
export function AlertRow({ a, compact, onNavigate }: { a: Alert; compact?: boolean; onNavigate?: () => void }) {
  const { can } = useSession();
  const nav = useNavigate();
  const job = useJob(a.job_id).data;
  const { map } = useTechMap();
  const ack = useAckAlert();
  const call = useCallTech();
  const brk = useMarkBreak();
  // Stable idempotency keys per user intent on this alert.
  const keys = useRef({ call: newKey(), brk: newKey(), ack: newKey() });
  const { title, sub } = alertText(a, job, map);
  const techId = a.tech_id ?? job?.assigned_tech_id ?? null;
  const acts = actionsFor({ ...a, tech_id: techId }).filter((x) => can(x.perm));

  // mutateAsync: the row unmounts as soon as it is hidden, and per-call mutate() callbacks would be dropped.
  const resolve = async (action: string, quietIfResolved = false) => {
    store.hide(a);
    if (!a.id) return;
    try {
      await ack.mutateAsync({ id: a.id, action, key: keys.current.ack });
    } catch (e) {
      if (e instanceof ApiError && e.code === 'already_resolved') {
        const by = (e.body as { resolved_by_name?: string } | null)?.resolved_by_name;
        if (!quietIfResolved || by) toastInfo(`Already handled${by ? ` by ${by}` : ''}`);
        return;
      }
      store.unhide(a); toastError(e, "Couldn't clear the alert");
    }
  };

  const run = async (x: AlertAction) => {
    const href = hrefFor(x.key, a);
    if (href) { onNavigate?.(); nav(href); return; }
    if (x.key === 'call_tech' && techId && !call.isPending) {
      try {
        await call.mutateAsync({ techId, jobId: a.job_id, key: keys.current.call });
        toastOk(`Calling ${map.get(techId)?.first_name ?? 'the tech'}`, 'Your phone rings first, then we connect the tech.');
        keys.current.call = newKey();
      } catch (e) { toastError(e, 'Call not placed'); }
    }
    if (x.key === 'mark_break' && techId && !brk.isPending) {
      try {
        await brk.mutateAsync({ techId, key: keys.current.brk });
        toastOk(`${map.get(techId)?.first_name ?? 'Tech'} marked on break`);
        await resolve('mark_break', true); // the server may already have closed it on the break itself
      } catch (e) { toastError(e, "Couldn't mark on break"); }
    }
  };
  const busy = (k: string) => (k === 'call_tech' && call.isPending) || (k === 'mark_break' && brk.isPending);

  return (
    <li className={`fx-alert fx-alert--${a.group}${compact ? ' is-compact' : ''}`}>
      <span className="fx-alert__icon" aria-hidden><Icon name={a.group === 'tech_stopped' ? 'siren' : a.group === 'parts_pending' ? 'box' : 'alert'} size={14} /></span>
      <div className="fx-alert__main">
        <strong>{title}</strong>
        <span>{[sub, ago(a.at)].filter(Boolean).join(' · ')}</span>
        <div className="fx-alert__acts">
          {acts.map((x) => (
            <button key={x.key} type="button" className={`stg-btn stg-btn--xs ${x.primary ? '' : 'stg-btn--ghost'}`} onClick={() => void run(x)} disabled={busy(x.key)} aria-busy={busy(x.key) || undefined}>
              <Icon name={x.icon} size={12} />{busy(x.key) ? `${x.label}…` : x.label}
            </button>
          ))}
          <button type="button" className="stg-btn stg-btn--xs stg-btn--ghost" onClick={() => void resolve('dismissed')} aria-label={`Dismiss: ${title}`}>Dismiss</button>
        </div>
      </div>
    </li>
  );
}

export function AlertsList({ alerts, compact, onNavigate, loading, failed }: { alerts: Alert[]; compact?: boolean; onNavigate?: () => void; loading?: boolean; failed?: boolean }) {
  if (loading) return <div className="app-loading" style={{ minHeight: 80 }}><span className="stg-dots"><i /><i /><i /></span></div>;
  return (
    <>
      {failed && <p className="fx-alerts__warn stg-muted"><Icon name="info" size={12} /> Alert inbox unavailable; showing live alerts only.</p>}
      {alerts.length ? <ul className="fx-alerts__list">{alerts.map((a) => <AlertRow key={a.key} a={a} compact={compact} onNavigate={onNavigate} />)}</ul>
        : <Empty icon="check-circle">No open alerts. Vans are moving and every job has a tech.</Empty>}
    </>
  );
}

/** Today panel. */
export function AlertsPanel() {
  const { alerts, inbox } = useAlerts();
  return <AlertsList alerts={alerts} loading={inbox.isLoading} failed={!!inbox.error} />;
}

// ─────────────────────────────────────────── the bell (top bar)
export function AlertsBell() {
  const { alerts, inbox } = useAlerts();
  const { map } = useTechMap();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  /** Job from whatever is already cached (the bell never fetches the job list itself). */
  const jobOf = (id: string | null): Job | undefined => {
    if (!id) return undefined;
    return qc.getQueryData<Job>(qk.job(id)) ?? qc.getQueriesData<Page<Job>>({ queryKey: qk.jobs() }).flatMap(([, d]) => d?.items ?? []).find((j) => j.id === id);
  };
  const root = useRef<HTMLDivElement>(null);

  // Live stream → alerts (and clear live ones whose situation is over).
  useStreamEvent('*', (e) => {
    store.clear((a) => clearedBy(e, a));
    const a = fromEvent(e);
    if (a) store.add(a);
  });

  // New alerts → one polite toast at most every 10 s (India's modal stack becomes a quiet batch).
  const known = useRef<Set<string> | null>(null);
  const pending = useRef<Alert[]>([]);
  const last = useRef(0);
  const timer = useRef<number | null>(null);
  useEffect(() => {
    if (!known.current) { if (inbox.isFetched) known.current = new Set(alerts.map((a) => a.key)); return; }
    const fresh = alerts.filter((a) => !known.current!.has(a.key));
    fresh.forEach((a) => known.current!.add(a.key));
    if (!fresh.length) return;
    pending.current.push(...fresh);
    const flush = () => {
      timer.current = null;
      const list = pending.current; pending.current = [];
      if (!list.length) return;
      last.current = Date.now();
      const first = alertText(list[0]!, jobOf(list[0]!.job_id), map);
      toast({ title: list.length === 1 ? first.title : `${list.length} new alerts`, body: list.length === 1 ? first.sub : list.slice(0, 3).map((x) => alertText(x, jobOf(x.job_id), map).title).join(' · '), tone: 'danger', duration: 8000 });
    };
    if (timer.current == null) timer.current = window.setTimeout(flush, Math.max(0, 10_000 - (Date.now() - last.current)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alerts, inbox.isFetched, map]);
  useEffect(() => () => { if (timer.current != null) clearTimeout(timer.current); }, []);

  useEffect(() => {
    if (!open) return;
    const down = (e: MouseEvent) => { if (!root.current?.contains(e.target as Node)) setOpen(false); };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    addEventListener('mousedown', down); addEventListener('keydown', key);
    return () => { removeEventListener('mousedown', down); removeEventListener('keydown', key); };
  }, [open]);

  const n = alerts.length;
  return (
    <div className="fx-alerts app-menu" ref={root}>
      <button type="button" className="fx-alerts__bell stg-iconbtn" aria-expanded={open} aria-haspopup="dialog" aria-label={n ? `Alerts, ${n} open` : 'Alerts, none open'} onClick={() => setOpen((o) => !o)}>
        <Icon name="alert" size={16} />
        {n > 0 && <i className="fx-alerts__count mono" aria-hidden>{n > 9 ? '9+' : n}</i>}
      </button>
      {open && (
        <div className="fx-alerts__pop" role="dialog" aria-label="Alerts">
          <header><b>Alerts</b><Link to="/today#alerts" className="stg-link" onClick={() => setOpen(false)}>Open Today</Link></header>
          <AlertsList alerts={alerts.slice(0, 8)} compact onNavigate={() => setOpen(false)} loading={inbox.isLoading} failed={!!inbox.error} />
        </div>
      )}
    </div>
  );
}
